import { useQuery } from '@tanstack/react-query';
import { format as DateFnsFormat, parseISO } from 'date-fns';
import IntlMessageFormat from 'intl-messageformat';
import * as React from 'react';

import { LocaleResponse } from 'api/api';

import { bootstrapConfig } from '~src/context/config';
import { GlobalStateContext, apiInstance } from '~src/global-state';
import { useIpc } from '~src/utils/ipc-utils';

import { TranslationsContext } from './context';
import { availableLocaleConfigs } from './localization';

export type FormatDateFn = typeof import('date-fns').format;

export type TranslateICUFn = (key: string, values?: { [key: string]: string | number }) => string;

type InjectedProps = {
  tICU: TranslateICUFn;
  formatDate: FormatDateFn;
};

export type TranslateProps = InjectedProps;

export const DEFAULT_CONTEXT_VARIABLES = {
  min: '',
  max: '',
  MAX_MONTH_NUMBER: '',
  MAX_WEEKDAY_NUMBER: '',
  dayOfMonthMin: '',
  dayOfMonthMax: '',
  INTERVAL_MONTH_NUMBER_MIN: '',
  INTERVAL_MONTH_NUMBER_MAX: '',
  INTERVAL_WEEKDAY_NUMBER_MIN: '',
  INTERVAL_WEEKDAY_NUMBER_MAX: '',
  dateMin: '',
  dateMax: '',
  strong: (insideText: string) => `<strong>${insideText}</strong>`,
};

export const useTranslations = () => {
  // TODO: Investigate - for some reason context is undefined on app start throwing errors
  const context = React.useContext(TranslationsContext) || {};

  return {
    locale: context.locale as string,
    locales: context.translations,
    tICU: (key: string, values?: { [key: string]: string | number }) => {
      const { translations = {}, locale = 'en-US' } = context;

      return new IntlMessageFormat(translations[key] || '', locale).format({
        ...DEFAULT_CONTEXT_VARIABLES,
        ...values,
      }) as string;
    },
    formatDate: (date: string | Date | number, format: string) => {
      const { locale = 'en-US' } = context;
      return DateFnsFormat(typeof date === 'string' ? parseISO(date) : date, format, {
        locale: availableLocaleConfigs[locale],
      });
    },
  };
};

export const TranslationsPapi: React.FC<React.PropsWithChildren> = ({ children }) => {
  const ipc = useIpc();

  const globalState = React.useContext(GlobalStateContext);
  const localeFromResponse = globalState?.locale;

  const locale = localeFromResponse ? localeFromResponse : bootstrapConfig.locale;

  const { data, dataUpdatedAt } = useQuery({
    queryKey: ['translations', locale],
    queryFn: async () => {
      const checkoutLocalization = apiInstance.v4.localizationGet(bootstrapConfig.sessionId, {
        locale,
      });
      const { data } = await checkoutLocalization;
      return data;
    },
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  React.useEffect(() => {
    if (data) {
      ipc.send({
        name: 'sync_translations',
        data,
      });
    }
  }, [dataUpdatedAt]);

  return <TranslationsContext.Provider value={data || {}}>{children}</TranslationsContext.Provider>;
};

export const TranslationsKiddo: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [localesState, setLocalesState] = React.useState<LocaleResponse>({});
  const ipc = useIpc();

  React.useEffect(() => {
    ipc.listen(payload => {
      if (payload.name === 'sync_translations') {
        setLocalesState(payload.data);
      }
    });
  }, []);

  return (
    <TranslationsContext.Provider value={localesState}>{children}</TranslationsContext.Provider>
  );
};
